/**
 * Voice Live provider config.
 *
 * Parses the neutral `provider-config` into the Voice Live adapter's typed shape
 * for **both** connection targets:
 *   - **model mode** (`targetType: "model"`) — a Realtime model such as `gpt-realtime`.
 *   - **agent mode** (`targetType: "agent"`) — a voice-enabled Foundry agent
 *     (`agentName` + `projectName`). Agent mode is Entra-only and the broker
 *     holds the credential; the widget never carries one. The service ignores
 *     `instructions` for agents, so the agent shape omits it.
 *
 * Field names mirror `@azure/ai-voicelive` session config so the broker/service
 * can apply them.
 */

import type { ProviderConfig } from "@microsoft/voice-widget-core-client";

/** Fields common to both model and agent targets. */
export interface VoiceLiveCommonConfig {
  /** Voice id, e.g. `"en-US-AvaNeural"`. Optional (broker/service default applies). */
  voice?: string;
  /** Voice type/kind (e.g. `"azure-standard"`); pairs with {@link VoiceLiveCommonConfig.voice}. */
  voiceType?: string;
  /** Spoken language(s) — a BCP-47 tag or a list for multilingual. */
  language?: string | string[];
  /** Turn-detection (VAD) mode, mapped to `turn_detection.type`. */
  vad?: string;
  /** Server-side input noise suppression; also applied to the mic capture. */
  noiseSuppression?: boolean;
  /** Echo cancellation; also applied to the mic capture. */
  echoCancellation?: boolean;
  /** Voice Live API version. */
  apiVersion?: string;
  /** Azure region. */
  region?: string;
}

/** Model-mode Voice Live target (e.g. `gpt-realtime`). */
export interface VoiceLiveModelConfig extends VoiceLiveCommonConfig {
  targetType: "model";
  /** Realtime model id, e.g. `"gpt-realtime"`. Required. */
  model: string;
  /** System instructions for the model. Optional (ignored in agent mode). */
  instructions?: string;
  /** Escape hatch: extra `session.update` fields applied verbatim. */
  sessionOverrides?: Record<string, unknown>;
}

/** Agent-mode Voice Live target — a voice-enabled Foundry agent (Entra-only). */
export interface VoiceLiveAgentConfig extends VoiceLiveCommonConfig {
  targetType: "agent";
  /** Foundry agent name. Required. */
  agentName: string;
  /** Foundry project name. Required. */
  projectName: string;
  /** Optional pinned agent version. */
  agentVersion?: string;
  // note: no `instructions` — the service ignores it for agents.
}

/** The config shape this adapter accepts (model or agent). */
export type VoiceLiveConfig = VoiceLiveModelConfig | VoiceLiveAgentConfig;

function parseCommon(raw: ProviderConfig): VoiceLiveCommonConfig {
  const common: VoiceLiveCommonConfig = {};
  if (typeof raw["voice"] === "string") common.voice = raw["voice"];
  if (typeof raw["voiceType"] === "string") common.voiceType = raw["voiceType"];
  const language = raw["language"];
  if (typeof language === "string") {
    common.language = language;
  } else if (Array.isArray(language) && language.every((l) => typeof l === "string")) {
    common.language = language as string[];
  }
  if (typeof raw["vad"] === "string") common.vad = raw["vad"];
  if (typeof raw["noiseSuppression"] === "boolean")
    common.noiseSuppression = raw["noiseSuppression"];
  if (typeof raw["echoCancellation"] === "boolean")
    common.echoCancellation = raw["echoCancellation"];
  if (typeof raw["apiVersion"] === "string") common.apiVersion = raw["apiVersion"];
  if (typeof raw["region"] === "string") common.region = raw["region"];
  return common;
}

/**
 * Validate/normalize raw `provider-config` into {@link VoiceLiveConfig}.
 * Throws on an unsupported target or missing required fields.
 */
export function parseVoiceLiveConfig(raw: ProviderConfig): VoiceLiveConfig {
  const targetType = raw["targetType"] ?? "model";

  if (targetType === "agent") {
    const agentName = raw["agentName"];
    const projectName = raw["projectName"];
    if (typeof agentName !== "string" || agentName.trim() === "") {
      throw new Error('provider-voicelive: agent mode requires a non-empty "agentName".');
    }
    if (typeof projectName !== "string" || projectName.trim() === "") {
      throw new Error('provider-voicelive: agent mode requires a non-empty "projectName".');
    }
    const config: VoiceLiveAgentConfig = {
      targetType: "agent",
      agentName,
      projectName,
      ...parseCommon(raw),
    };
    if (typeof raw["agentVersion"] === "string") config.agentVersion = raw["agentVersion"];
    return config;
  }

  if (targetType !== "model") {
    throw new Error(`provider-voicelive: unsupported targetType "${String(targetType)}".`);
  }

  const model = raw["model"];
  if (typeof model !== "string" || model.trim() === "") {
    throw new Error('provider-voicelive: model mode requires a non-empty "model".');
  }
  const config: VoiceLiveModelConfig = { targetType: "model", model, ...parseCommon(raw) };
  if (typeof raw["instructions"] === "string") config.instructions = raw["instructions"];
  const overrides = raw["sessionOverrides"];
  if (overrides && typeof overrides === "object" && !Array.isArray(overrides)) {
    config.sessionOverrides = overrides as Record<string, unknown>;
  }
  return config;
}
